import { get } from "lodash";
import moment from "moment";
import {
  CONTACT_POINT_SYSTEM,
  MAX_FEES_VALUE,
  REGEX_EXPS,
  SALE_MODES,
  SITUATION_PROFESSIONAL,
} from "utils/constants";
import { isMajor } from "utils/helpers/date";
import {
  createCityNameValidation,
  createContractNumberValidation,
  normalizeTextField,
  stripAllSpaces,
} from "utils/helpers/validations";
import { validateBIC, validateIBAN, validateNIR } from "utils/helpers/values";
import * as yup from "yup";
import { showExerciseFrame } from "./Forms/Activity";

const validation = (messages, { hasRia, hasPartner, checkUser }) => {
  const string = yup.string().typeError(messages.REQUIRED).nullable();
  const textField = yup
    .string()
    .transform(normalizeTextField)
    .typeError(messages.NOT_STRING)
    .nullable();
  const noSpaceString = yup.string().transform(stripAllSpaces);
  const nameValidation = textField
    .notRequired()
    .test(
      "is-valid-name",
      messages.INVALID_NAME,
      (value) => !value || REGEX_EXPS.name.test(value),
    );
  const requiredString = yup
    .string()
    .typeError(messages.REQUIRED)
    .required(messages.REQUIRED);

  const cityName = createCityNameValidation(messages, REGEX_EXPS);
  const contractNumber = createContractNumberValidation(messages, REGEX_EXPS);

  const relatedPersonValidationSchema = ({ birthDate }) =>
    yup.object().shape({
      identity: yup.object().shape({
        user: yup.object().shape({
          lastname: nameValidation,
          firstname: nameValidation,
          birthName: nameValidation,
          birthDate,
        }),
        socioProfessionalCategory: string,
        socialRegime: string,
        socialSecurityNumber: noSpaceString
          .typeError(messages.REQUIRED)
          .required(messages.REQUIRED)
          .test(
            "validateNIR",
            messages.INVALID_SOCIAL_SECURITY_NUMBER,
            function (value, ctx) {
              const result = validateNIR(value, this.parent);
              if (!result.valid)
                return ctx.createError({
                  message: messages[result.reason] || messages.INVALID_VALUE,
                });

              return true;
            },
          ),
        telecoms: yup.object().shape({
          [CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]: yup.object().shape({
            value: string,
          }),
          [CONTACT_POINT_SYSTEM.BIRTH_CITY]: yup.object().shape({
            value: cityName,
          }),
          [CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH]: yup.object().shape({
            value: string,
          }),
        }),
      }),
    });
  return yup.object().shape({
    createdDate: yup.date(),
    isPayerReceiver: yup.string().typeError(messages.NOT_STRING),
    additionalInfo: yup
      .object()
      .shape({
        brokerageFees: yup
          .number()
          .max(MAX_FEES_VALUE, `${messages.MAX} ${MAX_FEES_VALUE}`)
          .min(0, `${messages.MIN} 0`)
          .nullable()
          .optional(),
      })
      .optional(),
    project: yup.object().shape({
      projectType: yup.mixed().when("contract.saleMode", {
        is: (saleMode) => saleMode === SALE_MODES.DISTANT,
        then: string,
      }),
      contract: yup.object().shape({
        issuanceDate: yup.date(),
        caseType: string,
        saleMode: string,
      }),
      terminationRequest:
        hasRia &&
        yup.object().shape({
          dueDate: string,
          recipient: requiredString,
          reason: requiredString,
          contractNumber: createContractNumberValidation(messages, REGEX_EXPS, {
            required: true,
          }),
        }),
      contact: yup.object().shape({
        user: yup.object().shape({
          gender: string,
          lastname: nameValidation,
          firstname: nameValidation,
          birthName: nameValidation,
          birthDate: yup.date(),
        }),
        socialRegime: string,
        socialSecurityNumber: noSpaceString
          .typeError(messages.REQUIRED)
          .required(messages.REQUIRED)
          .test(
            "validateNIR",
            messages.INVALID_SOCIAL_SECURITY_NUMBER,
            function (value, ctx) {
              const result = validateNIR(value, this.parent);
              if (!result.valid)
                return ctx.createError({
                  message: messages[result.reason] || messages.INVALID_VALUE,
                });

              return true;
            },
          ),
        socioProfessionalCategory: requiredString,
        politicallyExposedPerson: yup.boolean().nullable(),
        exerciseFrame: yup.mixed().when("socioProfessionalCategory", {
          is: showExerciseFrame,
          then: string,
        }),
        siretNumber: string,
        fields: yup.object().when("socioProfessionalCategory", {
          is: (category) =>
            category && category !== SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB,
          then: yup.object().shape({
            affiliateOrganizationCode: yup
              .string()
              .typeError(messages.NOT_STRING)
              .nullable(),
            socioProfessionalSubCategory: requiredString,
            familySituation: requiredString,
          }),
          otherwise: yup.object().shape({
            affiliateOrganizationCode: yup
              .string()
              .typeError(messages.NOT_STRING)
              .nullable(),
            socioProfessionalSubCategory: string,
            familySituation: requiredString,
          }),
        }),
      }),
    }),
    telecoms: yup.object().shape({
      contact: yup.object().shape({
        [CONTACT_POINT_SYSTEM.STREET]: yup.object().shape({
          value: textField,
        }),
        [CONTACT_POINT_SYSTEM.COMPLEMENT]: yup.object().shape({
          value: textField,
        }),
        [CONTACT_POINT_SYSTEM.RESIDENCE]: yup.object().shape({
          value: textField,
        }),
        [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.PHONE]: yup.object().shape({
          value: noSpaceString
            .typeError(messages.REQUIRED)
            .nullable()
            .test("isValid", messages.INVALID_VALUE, function (value) {
              return value
                ? new RegExp(REGEX_EXPS.internationalMobilePhone).test(value)
                : true;
            }),
          // .test(
          //   "phoneConflict",
          //   messages.PHONE_CONFLICT,
          //   async function (value) {
          //     const isValidRegex = new RegExp(REGEX_EXPS.phone).test(value);
          //     if (!value || !isValidRegex) {
          //       return true;
          //     }
          // const {
          //   data: { user },
          // } = await checkUser({
          //   variables: {
          //     where: { phone: value },
          //   },
          // });
          // return !user?.id;
          // }
          // ),
        }),
        [CONTACT_POINT_SYSTEM.LANDLINE]: yup.object().shape({
          value: noSpaceString
            .typeError(messages.REQUIRED)
            .nullable()
            .test("isValid", messages.INVALID_VALUE, async function (value) {
              return value ? new RegExp(REGEX_EXPS.phone).test(value) : true;
            }),
        }),
        [CONTACT_POINT_SYSTEM.EMAIL]: yup.object().shape({
          value: yup.string().nullable().email(messages.EMAIL),
          // .test(
          //   "emailConflict",
          //   messages.EMAIL_CONFLICT,
          //   async function (value) {
          //     if (!value || !yup.string().email().isValidSync(value)) {
          //       return true;
          //     }
          //     const {
          //       data: { user },
          //     } = await checkUser({
          //       variables: {
          //         where: { email: value },
          //       },
          //     });
          //     return !user?.id;
          //   }
          // ),
        }),
      }),
      payer: yup.object().shape({
        [CONTACT_POINT_SYSTEM.STREET]: yup.object().shape({
          value: textField,
        }),
        [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
          value: string,
        }),
      }),
    }),
    spouse:
      hasPartner &&
      relatedPersonValidationSchema({
        birthDate: yup
          .date()
          .typeError(messages.REQUIRED)
          .max(isMajor, messages.RADIANCE.SPOUSE_AGE)
          .min(
            moment().subtract(100, "year").startOf("year"),
            messages.RADIANCE.SPOUSE_AGE,
          )
          .required(messages.REQUIRED),
      }),
    children: yup.array().of(
      yup.object().shape({
        identity: yup.object().shape({
          socialSecurityNumber: noSpaceString
            .typeError(messages.REQUIRED)
            .required(messages.REQUIRED)
            .test(
              "validateNIR",
              messages.INVALID_SOCIAL_SECURITY_NUMBER,
              function (value, ctx) {
                const result = validateNIR(value, this.parent);
                if (!result.valid)
                  return ctx.createError({
                    message: messages[result.reason] || messages.INVALID_VALUE,
                  });

                return true;
              },
            ),
          user: yup.object().shape({
            lastname: nameValidation,
            firstname: nameValidation,
            birthDate: yup
              .date()
              .typeError(messages.REQUIRED)
              .max(moment(), messages.MIN_TODAY)
              .required(messages.REQUIRED),
          }),
          socioProfessionalCategory: string,
          socialRegime: string,
          telecoms: yup.object().shape({
            [CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]: yup.object().shape({
              value: requiredString,
            }),
            [CONTACT_POINT_SYSTEM.BIRTH_CITY]: yup.object().shape({
              value: cityName,
            }),
            [CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH]: yup.object().shape({
              value: requiredString,
            }),
          }),
        }),
      }),
    ),
    payment: yup.object().shape({
      type: string,
      frequency: string,
      payer: yup.object().shape({
        BIC: noSpaceString
          .typeError(messages.NOT_STRING)
          .nullable()
          .test(
            "validateBIC",
            messages.INVALID_BIC || messages.INVALID_IBAN,
            function (value) {
              if (!value) return true;
              return validateBIC(value).valid;
            },
          ),
        IBAN: noSpaceString
          .typeError(messages.REQUIRED)
          .test("validateIBAN", messages.INVALID_IBAN, function (value, ctx) {
            const result = validateIBAN(value);
            if (!result.valid)
              return ctx.createError({
                message: get(messages, result.reason, messages.INVALID_VALUE),
              });

            return true;
          })
          .required(messages.REQUIRED),
        owner: yup.object().shape({
          isAdherent: yup.boolean().typeError(messages.NOT_BOOLEAN),
          firstName: nameValidation,
          lastName: nameValidation,
        }),
      }),
      receiver: yup.object().shape({
        BIC: noSpaceString
          .typeError(messages.NOT_STRING)
          .nullable()
          .test(
            "validateBIC",
            messages.INVALID_BIC || messages.INVALID_IBAN,
            function (value) {
              if (!value) return true;
              return validateBIC(value).valid;
            },
          ),
        IBAN: noSpaceString
          .typeError(messages.REQUIRED)
          .test("validateIBAN", messages.INVALID_IBAN, function (value, ctx) {
            const result = validateIBAN(value);
            if (!result.valid)
              return ctx.createError({
                message: get(messages, result.reason, messages.INVALID_VALUE),
              });

            return true;
          })
          .required(messages.REQUIRED),
        owner: yup.object().shape({
          firstName: nameValidation,
          lastName: nameValidation,
        }),
      }),
    }),
  });
};

export default validation;
